import React, { useState, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Auth } from '@aws-amplify/auth';
import { useHistory, useLocation } from 'react-router-dom';
import { Stack, Text, Box as Wrapper, Space, Grid, Image, Icon } from '@tonic-ui/react';
import { useTranslation, Trans } from 'react-i18next';

import NotificationBanner from '../../components/notification-banner/notification-banner';
import {
  FormContainer,
  InputField,
  Recaptcha,
  registrationValidation,
  SubmitButton,
  DropdownMenu,
} from '../../components/forms';
import TCCheckbox from './tc-checkbox';
import { getCommonType } from '../../store';
import { getTimeZoneAreaLocation } from '../../utils/timezone';
import filterMessage from '../../utils/message-filter';
import { countries } from '../../utils/countries';
import { supportedLocales } from '../../locales/supportedLocales';
import signUpBackground from '../../assets/images/cloudone_signup_bg.svg';
import cloudGraphic from '../../assets/landing/SVG/cloud_graphic.svg';
import { isGovCloudRegion } from '../../utils/helpers';
import RouteLink from '../../components/route-link';
import ExternalLink from '../../components/external-link/external-link';

interface FormProps {
  email: string;
  name: string;
  country: string;
  password: string;
  confirmPassword: string;
  recaptcha: string;
  recaptchaSitekey: string;
  termsAndConditions: false;
}

let deploymentTarget = '';
let recaptchaSitekey = '';

switch (DEPLOYMENT_TARGET) {
  case 'prod':
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_PROD || '';
    break;
  case 'stage':
    deploymentTarget = 'staging-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_STAGING || '';
    break;
  case 'drsim':
    deploymentTarget = 'drsim-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_DRSIM || '';
    break;
  case 'govcloud-prod':
    deploymentTarget = 'govcloud-prod-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_GOVCLOUD_PROD || '';
    break;
  case 'govcloud-staging':
    deploymentTarget = 'govcloud-staging-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_GOVCLOUD_STAGING || '';
    break;
  case 'alpha':
  case 'dev':
    deploymentTarget = 'dev-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_DEV || '';
    break;
  default:
    console.log('Should never reach here');
}

const Registration = () => {
  const [formErrors, setFormErrors] = useState({
    error: '',
  });
  const [refererUrl, setRefererUrl] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');

  const { t } = useTranslation();
  const history = useHistory();
  // Note that the line below explicitly doesn't use the react-router history because
  // for some reason beyond my ken it didn't work. This does, though.
  const { invitationToken = '', email: defaultEmail = '' } = window.history.state || {};
  const location = useLocation();
  const recaptchaRef = useRef();
  const focusRef = useRef(null);

  const {
    theme: { colorMode },
    locale,
  } = useSelector(
    (state) => ({
      theme: getCommonType(state, 'theme'),
      locale: getCommonType(state, 'locale'),
    }),
    shallowEqual
  );

  const isTrialPage = location?.pathname === '/trial';
  const isEndpointTrialPage = location?.pathname === '/endpoint-trial';

  useEffect(() => {
    const findReferrerCookie =
      document.cookie.length > 1 &&
      document.cookie.split('; ').find((cookie) => cookie.startsWith('_c1Ref'));
    const referrerPathname = findReferrerCookie ? findReferrerCookie.split('_c1Ref=')[1] : '';

    setRefererUrl(`${document.referrer}${decodeURIComponent(referrerPathname.slice(1))}`);
  }, []);

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current = focusRef.current.focus();
    }
  }, []);

  const handleSubmit = async ({
    email,
    name,
    country,
    password,
    setSubmitting,
    resetForm,
    recaptcha,
  }) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    const storedLocale = localStorage.getItem('C1C_locale');
    const preferredLanguage =
      storedLocale && supportedLocales.includes(storedLocale) ? storedLocale : 'en';

    // In Area/Location (city) format
    const timeZoneString = getTimeZoneAreaLocation();
    try {
      const { codeDeliveryDetails = {}, userConfirmed = false } = await Auth.signUp({
        username: email,
        password,
        attributes: {
          name: name,
          locale: preferredLanguage,
          zoneinfo: timeZoneString,
          'custom:country': country,
        },
        validationData: [
          {
            Name: 'recaptchaToken',
            Value: recaptcha,
          },
          {
            Name: 'validationToken',
            Value: invitationToken,
          },
        ],
      });

      if (isEndpointTrialPage) localStorage.setItem('C1C_ENDPOINT_TRIAL_ACCOUNT_CREATED', 'true');

      return history.push('/confirmation', {
        name,
        email,
        result: {
          // need to explicitly take only some attributes as others
          // are complex and can't be pushed through history
          codeDeliveryDetails: codeDeliveryDetails,
          userConfirmed: userConfirmed,
        },
      });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errMsg = !error.message ? error : error.message;
      console.error('Error: ', errMsg);

      setFormErrors({
        ...formErrors,
        error: filterMessage(error),
      });

      setSubmitting(false);
      resetForm();
      recaptchaRef.current.resetRecaptcha();
    }
  };

  const defaultValues: FormProps = {
    email: defaultEmail,
    name: '',
    country: '',
    password: '',
    confirmPassword: '',
    recaptcha: '',
    recaptchaSitekey: '',
    termsAndConditions: false,
  };

  return (
    <Grid
      gridTemplateColumns={
        isTrialPage || isEndpointTrialPage ? 'repeat(auto-fit, minmax(320px, 1fr))' : '1fr'
      }
      style={isTrialPage || isEndpointTrialPage ? { height: 'calc(100vh - 40px)' } : {}}
    >
      {isTrialPage || isEndpointTrialPage ? (
        <Wrapper backgroundImage={`url(${signUpBackground})`} backgroundSize="cover" color="white">
          <Stack
            spacing="4x"
            width="100%"
            maxWidth={{ xl: '650px' }}
            padding={{ sm: '0 10%', xl: '0 0 0 50px' }}
            margin={{ sm: '48px 0', lg: '50px 0', xl: '50px 50px 50px auto' }}
          >
            <Text
              size="5xl"
              lineHeight="normal"
              fontWeight="extralight"
              textAlign={{ sm: 'center', md: 'left' }}
            >
              <Trans
                i18nKey={
                  isEndpointTrialPage ? 'endpointTrial.signUpCloudOneBold' : 'signUpCloudOneBold'
                }
              />
            </Text>
            <Image
              src={cloudGraphic}
              id="hero-graphic"
              data-id="hero-graphic"
              width="90%"
              margin={{ sm: '0.5rem auto', md: '0.5rem 0' }}
              alt="Image of Trend Cloud One Services"
            />
            <Text
              size="6xl"
              lineHeight="normal"
              fontWeight="extralight"
              textAlign={{ sm: 'center', md: 'left' }}
              marginBottom="0.5rem"
            >
              <Trans
                i18nKey={
                  isEndpointTrialPage ? 'endpointTrial.signUpCloudOneText1' : 'signUpCloudOneText1'
                }
              />
            </Text>
            {isEndpointTrialPage ? (
              <>
                <Stack direction="row" spacing="4x" mb="4x" alignItems="start">
                  <Icon icon="check-circle-o" size="6x" />
                  <Text size="md">{t('endpointTrial.signUpCloudOneText2')}</Text>
                </Stack>
                <Stack direction="row" spacing="4x" mb="4x" alignItems="start">
                  <Icon icon="check-circle-o" size="6x" />
                  <Text size="md">{t('endpointTrial.signUpCloudOneText3')}</Text>
                </Stack>
                <Stack direction="row" pl="9x" spacing="4x" mb="4x" alignItems="start">
                  <Icon icon="check-circle-o" size="6x" />
                  <Text size="md">{t('endpointTrial.signUpCloudOneText3Subitem1')}</Text>
                </Stack>
                <Stack direction="row" pl="9x" spacing="4x" mb="4x" alignItems="start">
                  <Icon icon="check-circle-o" size="6x" />
                  <Text size="md">{t('endpointTrial.signUpCloudOneText3Subitem2')}</Text>
                </Stack>
              </>
            ) : (
              <>
                <Stack direction="row" spacing="4x" alignItems="start">
                  <Icon icon="check-circle-o" size="6x" />
                  <Text size="md">{t('signUpCloudOneText2')}</Text>
                </Stack>
                <Stack direction="row" spacing="4x" alignItems="start">
                  <Icon icon="check-circle-o" size="6x" />
                  <Text size="md">{t('signUpCloudOneText3')}</Text>
                </Stack>
                <Stack direction="row" spacing="4x" alignItems="start">
                  <Icon icon="check-circle-o" size="6x" />
                  <Text size="md">{t('signUpCloudOneText4')}</Text>
                </Stack>
              </>
            )}

            {locale === 'ja' && isEndpointTrialPage ? (
              <Text>{t('endpointTrial.seePricing')}</Text>
            ) : (
              <Wrapper fontSize="md" ml={isTrialPage ? '10x' : 0} mb="10x">
                <ExternalLink
                  href={
                    isEndpointTrialPage
                      ? 'https://www.trendmicro.com/en_us/business/get-info-form.html'
                      : 'https://aws.amazon.com/marketplace/pp/prodview-g232pyu6l55l4'
                  }
                  showIcon={false}
                  style={{
                    wordBreak: 'break-all',
                    color: 'inherit',
                    lineHeight: 'normal',
                    width: 'fit-content',
                  }}
                >
                  {isEndpointTrialPage ? t('endpointTrial.seePricing') : t('seePricing')}
                </ExternalLink>
              </Wrapper>
            )}
          </Stack>
        </Wrapper>
      ) : null}
      <Wrapper
        maxWidth={isTrialPage || isEndpointTrialPage ? '600px' : '850px'}
        margin={
          isTrialPage || isEndpointTrialPage
            ? { sm: '45px 20px', md: '50px 50px', lg: '50px 50px 0' }
            : '128px auto'
        }
      >
        {formErrors?.error &&
          (formErrors.error.includes('emailAlreadyExists') ? (
            <NotificationBanner
              name="registration-form-user-already-exists"
              severity="error"
              shouldOpen={true}
              htmlElement={
                <Trans
                  i18nKey={`cognitoErrorMessages.emailAlreadyExists`}
                  components={{
                    signinLink: <RouteLink href="/" />,
                  }}
                />
              }
            />
          ) : (
            <NotificationBanner
              name="registration-form"
              severity="error"
              shouldOpen={true}
              bannerText={formErrors.error}
            />
          ))}

        <FormContainer
          id="registration-form"
          customValidation={registrationValidation}
          initialValues={defaultValues}
          enableReinitialize={true}
          submitAction={(formPayload) => handleSubmit(formPayload)}
          validateOnChange={true}
        >
          <>
            <Stack spacing="4x">
              {isTrialPage || isEndpointTrialPage ? (
                <>
                  <Text size="2xl" lineHeight="normal" pb="6x">
                    <Trans i18nKey="signUpCloudOneText5" />
                  </Text>
                </>
              ) : (
                <Text size="2xl" lineHeight="xl" mt="32px">
                  {t('signUpCloudOne')}
                </Text>
              )}
            </Stack>
            <Stack
              spacing={isTrialPage || isEndpointTrialPage ? '2x' : '4x'}
              marginTop={isTrialPage || isEndpointTrialPage ? 0 : 28}
            >
              <Wrapper>
                <InputField
                  id="business-email"
                  type="email"
                  label={t('businessEmail')}
                  name="email"
                  isRequired={true}
                  value={defaultValues.email}
                  ref={focusRef}
                />
              </Wrapper>
              <Wrapper>
                <InputField id="name" type="text" label={t('name')} name="name" isRequired={true} />
              </Wrapper>
              <DropdownMenu
                options={countries}
                label={t('country')}
                placeholder={t('selectCountry')}
                name="country"
                maxHeight={200}
                isFullWidth={true}
                isRequired={true}
                isSearchable={true}
                onChange={(selectedId) => setSelectedCountry(selectedId)}
              />
              <Wrapper marginTop="2x">
                <InputField
                  id="password"
                  type="password"
                  label={t('password')}
                  name="password"
                  isRequired={true}
                  infoPopoverContent={
                    <>
                      <Text>{t('signupPasswordError.passwordInfoIconDescription1')}</Text>
                      <Wrapper textIndent="initial">
                        <ul>
                          <li>{t('signupPasswordError.passwordInfoIconDescription2')}</li>
                          <li>{t('signupPasswordError.passwordInfoIconDescription3')}</li>
                          <li>{t('signupPasswordError.passwordInfoIconDescription4')}</li>
                          <li>{t('signupPasswordError.passwordInfoIconDescription5')}</li>
                        </ul>
                      </Wrapper>
                    </>
                  }
                />
              </Wrapper>
              <Wrapper>
                <InputField
                  id="confirm-password"
                  type="password"
                  label={t('confirmPassword')}
                  name="confirmPassword"
                  isRequired={true}
                />
              </Wrapper>
            </Stack>
            <Stack spacing="7x">
              <Wrapper paddingTop={isTrialPage || isEndpointTrialPage ? 36 : 24}>
                <Recaptcha
                  ref={recaptchaRef}
                  name="recaptcha"
                  theme={colorMode}
                  siteKey={recaptchaSitekey}
                  locale={locale}
                />
              </Wrapper>
              <Wrapper>
                <TCCheckbox name="termsAndConditions" countryId={selectedCountry} />
              </Wrapper>
              <Wrapper>
                <SubmitButton
                  label={t('signUp')}
                  id="registration-form-submit-btn"
                  variant={isTrialPage || isEndpointTrialPage ? 'emphasis' : ''}
                  {...(isTrialPage || isEndpointTrialPage
                    ? { width: '100%', height: '50px', lineHeight: 'normal' }
                    : { width: '100%' })}
                />
              </Wrapper>
              <Text alignSelf="center">
                {t('alreadyUser')}
                <Space width="1x" />
                <RouteLink href="/">{t('signIn')}</RouteLink>
              </Text>
            </Stack>
          </>
        </FormContainer>
        {!isGovCloudRegion && (
          <iframe
            frameBorder="0"
            width="1"
            height="1"
            name="registration"
            title="c1c_tealium_iframe"
            id="c1c_tealium_iframe"
            src={`https://tsm.${deploymentTarget}cloudone.trendmicro.com/_auth/static-${BUILD_TIMESTAMP}/assets/tealium/index.html?referrer=${refererUrl}&url=${document.URL}`}
          />
        )}
      </Wrapper>
    </Grid>
  );
};

export default Registration;
